import { subscriptionService } from '../../../marketplace/services/subscriptionService';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => {
      throw new Error('Failed to load Razorpay script');
    };
    document.body.appendChild(script);
  });
};

const planIdToPlanType = (planId) => {
  const map = {
    pro: 'pro',
    'pro plus': 'pro_plus',
    'pro_plus': 'pro_plus',
    premium: 'premium',
  };
  return map[String(planId).toLowerCase()] || null;
};

/**
 * Open Razorpay subscription checkout (recurring).
 * Creates a Razorpay Plan + Subscription on the backend, then opens checkout with subscription_id.
 * Razorpay handles all payment methods (Cards, UPI, Net banking, Wallets).
 *
 * @param {Object} params
 * @param {Object} params.plan - Plan object (id, name) from pricing
 * @param {string} params.cycle - 'monthly' | 'yearly'
 * @param {Function} params.onSuccess
 * @param {Function} params.onError
 */
export const payWithRazorpay = async ({
  plan,
  cycle,
  billing,
  onSuccess,
  onError,
}) => {
  try {
    const planType = planIdToPlanType(plan?.id);
    if (!planType) {
      throw new Error('Invalid plan selected');
    }

    const createResponse = await subscriptionService.createSubscription(planType, cycle, billing);
    const { subscriptionId, keyId, mode } = createResponse.data;

    if (!subscriptionId || !keyId) {
      throw new Error('Could not create subscription. Please try again.');
    }

    const Razorpay = await loadRazorpayScript();

    const options = {
      key: keyId,
      subscription_id: subscriptionId,
      name: 'Infuber',
      description: `Subscription: ${plan?.name || planType} (${cycle})`,
      // Only force a card/payment-method re-authorization (a small token charge)
      // when recovering a pending/halted subscription. For a new subscription this
      // must NOT be set, otherwise Razorpay tries a ₹5 token auth which conflicts
      // with the real first-payment order amount.
      ...(mode === 'recovery' ? { subscription_card_change: true } : {}),
      handler: function () {
        if (onSuccess) onSuccess({ mode: mode || 'new' });
      },
      theme: { color: '#000000' },
      modal: {
        ondismiss: function () {
          if (onError) {
            onError(new Error('Payment cancelled'));
          }
        },
      },
    };

    const razorpay = new Razorpay(options);
    razorpay.open();
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Payment failed. Please try again.';
    if (onError) {
      onError(new Error(message));
    }
  }
};
